import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { useForgotPasswordAuth } from "../../api/effects/mutations/ForgotPassword/forgotPasswordAuth";
import { useLogin } from "../../api/effects/mutations/login";
import HeaderGuest from "../../components/Header/HeaderGuest";
import { WarningModal } from "../../components/WarningModal";
import { LoginPayload } from "../../models/User";
import UserState from "../../recoil/UserState";
import { setCookie } from "../../utils/cookies";
import { z } from "zod";


const forgotPasswordSchema = z.object({
  EMAIL: z.string().min(1, { message: "Email is required" }).email({
    message: "Must be a valid email address",
  }),
});

const RootGrid = styled(Grid)(({ theme }) => ({
  height: "90vh",
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/BackgroundJob.svg)`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100%",
  [theme.breakpoints.down("sm")]: {
    backgroundSize: "cover",
  },
}));

const SquareGrid = styled(Grid)({
  backgroundColor: "#FFF",
  height: "auto",
  width: "100%",
  maxWidth: "90vw",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "2%",
});

const ClearLink = styled(Link)({
  color: "#150A33",
  textDecoration: "none",
  fontSize: ".8em",
  padding: "3%",
});

const ItemGrid = styled(Grid)({
  width: "100%",
  textAlign: "center",
});

const ItemTitleGrid = styled(Grid)({
  width: "100%",
  textAlign: "center",
  paddingBottom: "4vh",
});

const ButtonFull = styled(Button)({
  marginTop: "3vh",
  marginBottom: "1vh",
  width: "60%",
});

const TextFieldFull = styled(TextField)({
  width: "90%",
});

export default function ForgotPasswordPage() {
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [warningModalState, setWarningModalState] = useState(false);
  const [warningModalText, setWarningModalText] = useState("");

  const isCoolingDown = countdown > 0;

  const { mutateAsync: forgotPasswordAuth } = useForgotPasswordAuth({
    onSuccess(data) {
      localStorage.setItem("lastUsedEmail", email);
      const newAttempts = attempts + 1;

      if (newAttempts >= 5) {
        const longCooldown = 15 * 60;
        setCountdown(longCooldown);
        const expiryTime = Date.now() + longCooldown * 1000;
        localStorage.setItem("sendEmailCooldown", expiryTime.toString());
        setAttempts(0);
        localStorage.setItem("forgotPasswordAttempts", "0");
      } else {
        const shortCooldown = 60;
        setCountdown(shortCooldown);
        const expiryTime = Date.now() + shortCooldown * 1000;
        localStorage.setItem("sendEmailCooldown", expiryTime.toString());
        setAttempts(newAttempts);
        localStorage.setItem("forgotPasswordAttempts", newAttempts.toString());
      }
      setWarningModalText(data);
      setWarningModalState(true);
      console.log(`Success, attempt number ${newAttempts}`)
    },
    onError(data: any) {
      setWarningModalText(data.response.data);
      setWarningModalState(true);
      console.log("Error");
    },
  });

  useEffect(() => {
    const storedCooldown = localStorage.getItem("sendEmailCooldown");
    const storedEmail = localStorage.getItem('lastUsedEmail');
    const storedAttempts = localStorage.getItem("forgotPasswordAttempts");
    if (storedEmail) {
      setEmail(storedEmail);
    }
    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts, 10));
    }
    if (storedCooldown) {
      const expiryTime = parseInt(storedCooldown, 10);
      const now = Date.now();
      if (expiryTime > now) {
        const remaining = Math.ceil((expiryTime - now) / 1000);
        setCountdown(remaining);
      } else {
        localStorage.removeItem("sendEmailCooldown");
      }
    }
  }, []);


  useEffect(() => {
    if (!isCoolingDown) {
      return;
    }
    const intervalId = setInterval(() => {
      setCountdown((prevCountdown) => prevCountdown - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isCoolingDown]);

  // Handle button click
  const forgotPasswordPush = () => {
    const validationResult = forgotPasswordSchema.safeParse({ EMAIL: email });
    if (!validationResult.success) {
      setError(validationResult.error.issues[0].message);
      return;
    }
    setError("");
    if (isCoolingDown && attempts === 0) {
      setWarningModalText("Too many attempts, please try again later.");
      setWarningModalState(true);
      return;
    }

    forgotPasswordAuth({ EMAIL: email });
  };
  return (
    <>
      <HeaderGuest />
      <WarningModal
        open={warningModalState}
        handleClose={() => setWarningModalState(false)}
        text={warningModalText}
      />
      <RootGrid container justifyContent="center" alignItems="center">
        <SquareGrid
          item={true}
          xs={12}
          sm={9}
          md={6}
          lg={3}
          spacing={1}
          container
          alignItems="center"
          justifyContent="center"
          direction="column"
        >
          <ItemTitleGrid item>
            <Typography variant="h4">Forgot Password</Typography>
          </ItemTitleGrid>
          <ItemGrid item>
            <TextFieldFull
              variant="outlined"
              label="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmail(e.target.value);
                if (error) {
                  setError("");
                }
              }}
              error={!!error}
              helperText={error}
            />
          </ItemGrid>
          <ItemGrid item>
            <ButtonFull variant="contained"
              onClick={forgotPasswordPush}
              disabled={isCoolingDown && attempts > 0}
            >
              {isCoolingDown ? `Wait ${countdown}s` : "Send Email"}
            </ButtonFull>
          </ItemGrid>
        </SquareGrid>
      </RootGrid>
    </>
  );
}
